import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

/**
 * Month grid, styled to the layout chosen as the app-wide reference: month +
 * year on the left, chevrons on the right, a muted MON–SUN row, and the
 * selected day as a filled circle.
 *
 * The circle is CLAY, not the iOS system blue of the reference screenshot —
 * that blue belongs to the OS picker this replaces and cannot be branded.
 * Everything here is tokenised, so it renders identically on iOS, Android
 * and desktop, which the native control does not.
 *
 * This was previously raw Tailwind greys with `bg-red-600` selected days —
 * the pre-Kanfy brand red, which doubled as the destructive colour.
 */
function Calendar({
  className,
  classNames,
  components,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      // Monday-first with three-letter headers, matching the reference
      // layout. react-day-picker defaults to Sunday-first two-letter ("Su
      // Mo"), which is a US convention and not what the markets we operate
      // in read as a week.
      weekStartsOn={1}
      formatters={{
        formatWeekdayName: (day) =>
          day.toLocaleDateString(undefined, { weekday: "short" }).slice(0, 3),
      }}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-2 sm:space-x-3 sm:space-y-0",
        month: "space-y-2",
        caption: "flex justify-between items-center pt-1 pb-2",
        caption_label: "text-base font-bold tracking-tight text-foreground",
        nav: "flex items-center gap-1",
        nav_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-8 w-8 bg-transparent p-0 text-foreground hover:bg-muted rounded-full",
        ),
        nav_button_previous: "",
        nav_button_next: "",
        table: "border-collapse w-full",
        head_row: "flex",
        head_cell:
          "w-10 h-8 flex items-center justify-center text-muted-foreground font-semibold text-[10px] uppercase tracking-wider",
        row: "flex w-full mt-0.5",
        cell: "w-10 h-9 flex items-center justify-center text-sm p-0 focus-within:relative focus-within:z-20",
        // Deliberately NOT buttonVariants({variant:"ghost"}): that contributes
        // `bg-transparent`, which collides with day_selected's `bg-primary` at
        // equal specificity and wins on stylesheet order — the clay disc never
        // painted and the cream numeral sat on white at ~1.1:1, making the
        // selected day invisible. These are the ghost styles we actually want,
        // minus the background.
        day: "inline-flex items-center justify-center h-9 w-9 p-0 font-medium text-sm rounded-full text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none aria-selected:opacity-100",
        day_range_end: "day-range-end",
        // Clay disc + on-action label: the same pairing as a primary button,
        // so the number clears AA (4.69:1) rather than sitting on a tint.
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-full",
        // Today gets a ring, not a fill, so it can never be mistaken for the
        // current selection.
        day_today: "font-bold ring-1 ring-inset ring-border",
        day_outside:
          "day-outside text-muted-foreground/40 aria-selected:bg-transparent",
        day_disabled: "text-muted-foreground/40 opacity-50 cursor-not-allowed",
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        caption_dropdowns: "flex items-center gap-2 relative",
        vhidden: "hidden",
        dropdown: "absolute inset-0 z-10 w-full cursor-pointer opacity-0",
        dropdown_month:
          "relative inline-flex items-center rounded-lg border border-border bg-background px-2 py-1 text-sm font-semibold text-foreground",
        dropdown_year:
          "relative inline-flex items-center rounded-lg border border-border bg-background px-2 py-1 text-sm font-semibold text-foreground",
        dropdown_icon: "ml-1 opacity-60",
        ...classNames,
      }}
      components={{
        IconLeft: () => <ChevronLeft className="h-4 w-4" />,
        IconRight: () => <ChevronRight className="h-4 w-4" />,
        ...components,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
